import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import { isNaN } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Select } from '~/components/Form';
import { COMPLEXIDADE_SENHA } from '~/constants/modules';
import { t, can } from '~/helpers';
import { updateData } from '~/store/modules/clienteConfig/actions';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.clienteConfig);

  const initials = useMemo(
    () => (create ? data : { ...find, ...data }),
    [create, data, find]
  );
  const [modoJuncaoAbastecimento, setModoJuncaoAbastecimento] = useState(
    Number(initials.juntaabastsequencialclienteconfig || 0)
  );
  const [tempoJuncaoAbastecimento, setTempoJuncaoAbastecimento] = useState(
    initials.minutosjuntaabastclienteconfig ?? 30
  );

  function handleModoJuncaoAbastecimento(value) {
    const novoModo = Number(value);

    if (novoModo !== modoJuncaoAbastecimento) {
      setTempoJuncaoAbastecimento(30);
    }
  }

  async function handleSubmit(param) {
    try {
      const dadosFormulario = {
        ...param,
        juntaabastsequencialclienteconfig: modoJuncaoAbastecimento,
        juntaabastlocomotivasequencialclienteconfig: modoJuncaoAbastecimento,
        minutosjuntaabastclienteconfig: Number(tempoJuncaoAbastecimento),
      };

      const schema = Yup.object().shape({
        maximoerrosinclinacaodbicclienteconfig: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .moreThan(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          .lessThan(
            10000000,
            t('s.geral.validacao.valormaximo').replace('%s', 9999999)
          ),

        percentualcompartimentoclienteconfig: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .moreThan(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          .lessThan(
            10000000,
            t('s.geral.validacao.valormaximo').replace('%s', 9999999)
          ),

        diacortecotaclienteconfig: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .moreThan(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          .lessThan(
            10000000,
            t('s.geral.validacao.valormaximo').replace('%s', 9999999)
          ),

        kmdvecsolicitadigitacaoclienteconfig: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .moreThan(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          .lessThan(
            10000000,
            t('s.geral.validacao.valormaximo').replace('%s', 9999999)
          ),

        horadvecsolicitadigitacaoclienteconfig: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .moreThan(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          .lessThan(
            10000000,
            t('s.geral.validacao.valormaximo').replace('%s', 9999999)
          ),

        varmaxfatorafericaodvec: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .moreThan(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          .lessThan(
            10000000,
            t('s.geral.validacao.valormaximo').replace('%s', 9999999)
          ),

        kmdvecpulsoentraafericaoclienteconfig: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .moreThan(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          .lessThan(
            10000000,
            t('s.geral.validacao.valormaximo').replace('%s', 9999999)
          ),
        kmdveccanparcialentraafericaoclienteconfig: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .moreThan(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          .lessThan(
            10000000,
            t('s.geral.validacao.valormaximo').replace('%s', 9999999)
          ),

        horadvecentraafericaoclienteconfig: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .integer(t('s.geral.validacao.deveserinteiro'))
          .moreThan(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
          .lessThan(
            10000000,
            t('s.geral.validacao.valormaximo').replace('%s', 9999999)
          ),

        minutosjuntaabastclienteconfig:
          modoJuncaoAbastecimento === 1 || modoJuncaoAbastecimento === 2
            ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .transform((value) => (isNaN(value) ? undefined : value))
              .integer(t('s.geral.validacao.deveserinteiro'))
              .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
              .max(60, t('s.geral.validacao.valormaximo').replace('%s', 60))
            : Yup.number().notRequired(),
      });

      await schema.validate(dadosFormulario, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...dadosFormulario,
        idcliente: client,
      };

      await dispatch(updateData(objSend, params));
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  useEffect(() => {
    if (!loading) {
      setTempoJuncaoAbastecimento(
        initials.minutosjuntaabastclienteconfig ?? 30
      );
    }
  }, [loading, initials.minutosjuntaabastclienteconfig]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Select
            name="formaidfrentistaclienteconfig"
            label="Forma ID Frentista"
            options={[
              { value: 0, label: 'Não solicita frentista' },
              { value: 1, label: 'Solicita código do frentista' },
              { value: 3, label: 'Solicita código/senha do frentista' },
              { value: 4, label: 'Solicita TAG do frentista' },
              { value: 5, label: 'Solicita código OU TAG do frentista' },
              { value: 7, label: 'Solicita código/senha OU TAG do frentista' },
              { value: 12, label: 'Solicita TAG/senha do frentista' },
              {
                value: 15,
                label: 'Solicita código/senha ou TAG/senha do frentista',
              },
            ]}
          />

          <Select
            name="formaidfrentistaabastclienteconfig"
            label="Forma ID Frentista Abastecimento"
            options={[
              { value: 0, label: 'Não solicita frentista' },
              { value: 1, label: 'Solicita código do frentista' },
              { value: 3, label: 'Solicita código/senha do frentista' },
              { value: 4, label: 'Solicita TAG do frentista' },
              { value: 5, label: 'Solicita código OU TAG do frentista' },
              { value: 7, label: 'Solicita código/senha OU TAG do frentista' },
              { value: 12, label: 'Solicita TAG/senha do frentista' },
              {
                value: 15,
                label: 'Solicita código/senha ou TAG/senha do frentista',
              },
            ]}
          />

          <Select
            name="formaidmotoristaclienteconfig"
            label="Forma ID Motorista"
            options={[
              { value: 0, label: 'Não solicita motorista' },
              {
                value: 1,
                label: 'Solicita apenas o codigo para qualquer veículo',
              },
              {
                value: 2,
                label: 'Solicita codigo e senha para qualquer veículo',
              },
              {
                value: 3,
                label: 'Solicita apenas o codigo para veículos próprios',
              },
              {
                value: 4,
                label: 'Solicita codigo e senha para veículos próprios',
              },
              {
                value: 5,
                label: 'Solicita apenas o codigo para veículos terceiros',
              },
              {
                value: 6,
                label: 'Solicita codigo e senha para veículos terceiros',
              },
            ]}
          />

          <Select
            name="formaidcentrocusto"
            label="Solicita Centro de Custo no terminal"
            options={[
              { value: 0, label: 'Não' },
              { value: 1, label: 'Sim' },
            ]}
          />

          <Select
            name="confirmatotrodadoclienteconfig"
            label="Solicita confirmação totalizador rotado no terminal"
            options={[
              { value: 0, label: 'Não' },
              { value: 1, label: 'Sim' },
            ]}
          />

          <Select
            name="logdvecclienteconfig"
            label="Log irregularidade DBIC"
            options={[
              { value: 0, label: 'Não' },
              { value: 1, label: 'Sim' },
            ]}
          />

          <Select
            name="solicitacampolivremudamodoclienteconfig"
            label="Solicita campo livre ao mudar modo"
            options={[
              { value: 'N', label: 'Não' },
              { value: 'S', label: 'Sim' },
            ]}
          />

          <Select
            name="acoesirregularidadedbicclienteconfig"
            label="Bloqueia bico irregularidade DBIC"
            options={[
              { value: 0, label: 'Não' },
              { value: 1, label: 'Sim' },
            ]}
          />

          <Select
            name="dbic4bloqueiaveiculoclienteconfig"
            label="Bloqueia veículo irregularidade DBIC"
            options={[
              { value: 0, label: 'Não' },
              { value: 1, label: 'Sim' },
            ]}
          />

          <Select
            name="dbic4bloqueiafrentistaclienteconfig"
            label="Bloqueia frentista irregularidade DBIC"
            options={[
              { value: 0, label: 'Não' },
              { value: 1, label: 'Sim' },
            ]}
          />

          <Input
            name="maximoerrosinclinacaodbicclienteconfig"
            label="Qtd. erros DBIC para bloqueio"
            type="number"
          />

          <Input
            name="percentualcompartimentoclienteconfig"
            label="Percentual compartimento cheio lubrificação"
            type="number"
          />

          <Select
            name="juntaabastsequencialclienteconfig"
            label="Junta abastecimentos em sequência"
            options={[
              { value: 0, label: 'Não realizar a junção' },
              {
                value: 1,
                label:
                  'Realizar junção com atraso na disponibilização para integração',
              },
              {
                value: 2,
                label: 'Realizar junção com retransmissão para integração',
              },
            ]}
            getValue={(value) => setModoJuncaoAbastecimento(Number(value))}
            onValueChange={handleModoJuncaoAbastecimento}
          />

          {(modoJuncaoAbastecimento === 1 ||
            modoJuncaoAbastecimento === 2) && (
            <Input
              name="minutosjuntaabastclienteconfig"
              label="Tempo de junção (minutos)"
              type="number"
              initialValue={tempoJuncaoAbastecimento}
              getValue={setTempoJuncaoAbastecimento}
            />
          )}

          <Input
            name="diacortecotaclienteconfig"
            label="Dia corte controle cotas"
            type="number"
          />

          <Select
            name="grupotanqueclienteconfig"
            label="Controla grupo de tanques"
            options={[
              { value: 'N', label: 'Não' },
              { value: 'S', label: 'Sim' },
            ]}
          />

          <Select
            name="exibeosclienteconfig"
            label="Solicita OS no terminal"
            options={[
              { value: 0, label: 'Não' },
              { value: 1, label: 'Sim' },
            ]}
          />

          <Select
            name="formadigitacaofrotaclienteconfig"
            label="Digitação de frota na liberação de abastecimento"
            options={[
              { value: 0, label: '' },
              { value: 1, label: 'Conforme cadastro do veículo' },
            ]}
          />

          <Select
            name="leitortagfrentistaclienteconfig"
            label="Leitor cartão frentista"
            options={[
              { value: 0, label: 'Não' },
              { value: 1, label: 'Sim' },
            ]}
          />

          <Select
            name="leitortagmotoristaclienteconfig"
            label="Leitor cartão motorista"
            options={[
              { value: 0, label: 'Não' },
              { value: 1, label: 'Sim' },
            ]}
          />

          <Input
            name="kmdvecsolicitadigitacaoclienteconfig"
            label="Km para solicitação de digitação DVEC"
            type="number"
          />

          <Input
            name="horadvecsolicitadigitacaoclienteconfig"
            label="Tempo para solicitação de digitação DVEC"
            type="number"
          />

          <Input
            name="varmaxfatorafericaodvec"
            label="Variação máxima do fator de aferição DVEC"
            type="number"
          />

          <Input
            name="kmdvecpulsoentraafericaoclienteconfig"
            label="Km para DVEC sinal entrar em aferição"
            type="number"
          />

          <Input
            name="kmdveccanparcialentraafericaoclienteconfig"
            label="Km para DVEC parcial entrar em aferição"
            type="number"
          />

          <Input
            name="horadvecentraafericaoclienteconfig"
            label="Tempo para DVEC entrar em aferição"
            type="number"
          />

          <Select
            name="associadvecentradaautoafericaodvechorimentoclienteconfig"
            label="Associa DVEC automaticamente na entrada da aferição"
            options={[
              { value: 'N', label: 'Não' },
              { value: 'S', label: 'Sim' },
            ]}
          />

          <Select
            name="controlarestricaotag"
            label="Controla restrição de TAG"
            options={[
              { value: 0, label: 'Não utiliza controle de restricao tag' },
              {
                value: 1,
                label: 'Utiliza e não permite reutilização das tags',
              },
              { value: 2, label: 'Utiliza e permite a reutilização de tags' },
            ]}
          />

          <Select
            name="exibebloqtngveicclienteconfig"
            label="Bloqueia abastecimento acima da capacidade do tanque"
            options={[
              { value: '', label: '' },
              { value: 'N', label: 'Não' },
              { value: 'S', label: 'Sim' },
            ]}
          />

          <Select
            name="habilitanivelfrentistaterminalclienteconfig"
            label="Habilita perfil de nível de acesso de frentista nos terminais"
            options={[
              { value: 'N', label: 'Não' },
              { value: 'S', label: 'Sim' },
            ]}
          />

          <Select
            name="descontaabastclienteconfig"
            label="Desconta Abastecimento"
            options={[
              { value: 0, label: 'Não' },
              { value: 1, label: 'Sim' },
            ]}
          />

          <Select
            name="ativaloginazureclienteconfig"
            label="Tipo de login"
            options={
              can(COMPLEXIDADE_SENHA)
                ? [{ value: 'N', label: 'SCA+' }]
                : [
                    { value: 'N', label: 'SCA+' },
                    { value: 'S', label: 'Azure' },
                  ]
            }
          />

          <Input name="azureclientidclienteconfig" label="Client ID" />
          <Input name="azuretenandidclienteconfig" label="Tenant ID" />
          <Input name="azureresponsetypeclienteconfig" label="Response Type" />
          <Input name="azureresponsemodeclienteconfig" label="Response Mode" />
          <Input name="azurescopeclienteconfig" label="Scope" />
          <Input name="azureredirecturiclienteconfig" label="Redirect URL" />

          <Input
            name="tokensstelematicaclienteconfig"
            label="Token SS Telemática"
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
